import { Secret } from "../../schema/types/secret/entity";
import { linksOf, request } from "../../services/cpln";
import { parseLink } from "../../utils/linkParser/linkParser";
import { ReleaseRevision, ReleaseRevisionResource } from "./types/release";
import { TemplateVersion, Template } from "./types/template";
import { ungzip } from "pako";

export const fetchLegacyReleaseItems = fetchReleaseItems;

export async function fetchReleaseItems(resources: ReleaseRevisionResource[]): Promise<any[]> {
  const promises = resources.map((r) =>
    request({ url: r.link })
      .then((r) => r.data)
      .catch((e) => {
        if (e.response?.status === 404) {
          const { name } = parseLink(r.link, { useInputCtx: true });
          return { id: r.id, name: name, link: r.link, kind: r.kind, version: r.version, notFound: true };
        }
        throw e;
      }),
  );
  const promiseResults = await Promise.all(promises);
  return promiseResults;
}

export function getVersions(template: Template) {
  if (!template) {
    return [];
  }
  return Object.keys(template.versions);
}

export function getTemplateVersion(template: Template, version: string): TemplateVersion | null {
  if (!template) {
    return null;
  }
  const versions = getVersions(template);
  if (!versions.includes(version)) {
    return null;
  }
  return template.versions[version];
}

export function decodeRevisionFrom(secret: Secret): ReleaseRevision | undefined {
  try {
    if (!secret.data || !(secret.data as any).payload) {
      console.log("ERROR: Invalid release revision secret data.");
      return;
    }

    const base64data = (secret.data as any).payload!;
    const binary = atob(base64data);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const decompressed = ungzip(bytes, { to: "string" });
    const decompressedObject = JSON.parse(decompressed) as ReleaseRevision;
    decompressedObject.secretLink = linksOf(secret).self;
    return decompressedObject;
  } catch (e) {
    console.log("decode fn error", e.message);
    return;
  }
}

export function getMarketplaceErrorMessage(e: any) {
  let errorMessage = e.response?.data?.message;
  if (!errorMessage) {
    errorMessage = e.message;
  }

  // Extract the main error message
  let mainError = "No main error found";
  let innerMessages: string[] = [];

  // Check if there's an ERROR: prefix
  if (errorMessage.includes("ERROR:")) {
    // First, try to extract error with JSON array (original pattern)
    const mainErrorWithArrayMatch = errorMessage.match(/ERROR:[\s\S]*?(?=\[)/);

    if (mainErrorWithArrayMatch) {
      // Original case: ERROR followed by JSON array
      mainError = mainErrorWithArrayMatch[0].trim().replace("ERROR: ", "");

      // Extract the JSON array part - use greedy match for nested objects
      const arrayMatch = errorMessage.match(/\[[\s\S]*\]/);
      if (arrayMatch) {
        try {
          const innerArray = JSON.parse(arrayMatch[0]);
          if (Array.isArray(innerArray)) {
            // Preserve full error details including status, message, and details
            innerMessages = innerArray.map((obj) => {
              const parts: string[] = [];
              if (obj.status) {
                parts.push(`Status ${obj.status}`);
              }
              if (obj.message) {
                parts.push(obj.message);
              }
              if (obj.details && Array.isArray(obj.details) && obj.details.length > 0) {
                const detailMessages = obj.details
                  .map((d: any) => d.message || JSON.stringify(d))
                  .join(", ");
                parts.push(`Details: ${detailMessages}`);
              }
              return parts.join(" - ");
            });
          }
        } catch (parseError) {
          // If JSON parsing fails, include the raw JSON string for visibility
          innerMessages = [arrayMatch[0]];
        }
      }
    } else {
      // New case: ERROR without JSON array - extract everything after ERROR:
      // Use [\s\S]+ to match across newlines, then trim whitespace
      const simpleErrorMatch = errorMessage.match(/ERROR:\s*([\s\S]+)/);
      if (simpleErrorMatch) {
        mainError = simpleErrorMatch[1].trim();
      }
    }
  } else {
    // No ERROR: prefix found, use the entire message
    mainError = errorMessage;
  }

  return [mainError, ...innerMessages];
}
